import React, { useState, useEffect } from 'react';

import { t } from 'i18next';
import { Box, Typography, LinearProgress } from '@datanomik/commons-ui';
import { keyframes } from '@emotion/react';

import { DatanomikIsoBlue } from '@/views/common/Logo';
import { DatanomikIsoWhite } from '@/views/common/Logo';

const breathe = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.85; }
  50% { transform: scale(1.04); opacity: 1; }
`;

const pulseGlow = keyframes`
  0%, 100% { opacity: 0.04; transform: scale(1); }
  50% { opacity: 0.08; transform: scale(1.05); }
`;

const zoomIn = keyframes`
  from { opacity: 0; transform: scale(1.05); }
  to { opacity: 1; transform: scale(1); }
`;

const shimmerText = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const themes = {
  light: {
    background: 'linear-gradient(145deg, #f8f9fc 0%, #eef1f8 35%, #e8ecf6 65%, #f0f2f8 100%)',
    Logo: DatanomikIsoBlue,
    orbs: [
      { bottom: '18%', left: '8%', width: 160, height: 160, bgcolor: '#6366f1', blur: 85, delay: 0 },
      { top: '12%', right: '10%', width: 140, height: 140, bgcolor: '#2845D2', blur: 90, delay: 3 },
    ],
    progressTrack: 'rgba(40, 69, 210, 0.06)',
    progressBar: 'rgba(40, 69, 210, 0.25)',
    shimmer: 'linear-gradient(90deg, rgba(40,69,210,0.35) 0%, rgba(40,69,210,0.7) 25%, rgba(99,102,241,0.9) 50%, rgba(40,69,210,0.7) 75%, rgba(40,69,210,0.35) 100%)',
  },
  dark: {
    background: 'linear-gradient(160deg, #0f1535 0%, #1a2260 40%, #2845D2 100%)',
    Logo: DatanomikIsoWhite,
    orbs: [
      { bottom: '20%', left: '8%', width: 120, height: 120, bgcolor: 'rgba(99,102,241,0.5)', blur: 60, delay: 0 },
      { top: '15%', right: '12%', width: 100, height: 100, bgcolor: 'rgba(167,139,250,0.3)', blur: 55, delay: 3 },
    ],
    progressTrack: 'rgba(255,255,255,0.08)',
    progressBar: 'rgba(255,255,255,0.4)',
    shimmer: 'linear-gradient(90deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.6) 25%, rgba(255,255,255,0.85) 50%, rgba(255,255,255,0.6) 75%, rgba(255,255,255,0.3) 100%)',
  },
};

export const LoadingPageNew = ({ leaving = false, variant = 'light' }) => {
  const [exitStarted, setExitStarted] = useState(false);

  const theme = themes[variant] || themes.light;
  const { Logo } = theme;

  // When leaving becomes true, wait a frame then start exit animation
  useEffect(() => {
    if (!leaving) return undefined;
    let id2;
    const id1 = requestAnimationFrame(() => {
      id2 = requestAnimationFrame(() => setExitStarted(true));
    });
    return () => { cancelAnimationFrame(id1); cancelAnimationFrame(id2); };
  }, [leaving]);

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        background: theme.background,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        zIndex: 9999,
        animation: leaving ? 'none' : `${zoomIn} 0.6s ease-out`,
        opacity: exitStarted ? 0 : 1,
        transform: exitStarted ? 'scale(1.06)' : 'scale(1)',
        transition: leaving ? 'opacity 500ms ease, transform 500ms ease' : 'none',
        overflow: 'hidden',
        pointerEvents: exitStarted ? 'none' : 'auto',
      }}
    >
      {/* Ambient orbs */}
      {theme.orbs.map((orb, i) => (
        <Box
          key={i}
          sx={{
            position: 'absolute',
            top: orb.top,
            right: orb.right,
            bottom: orb.bottom,
            left: orb.left,
            width: orb.width,
            height: orb.height,
            borderRadius: '50%',
            bgcolor: orb.bgcolor,
            filter: `blur(${orb.blur}px)`,
            animation: `${pulseGlow} ${8 + i * 2}s ease-in-out infinite ${orb.delay || 0}s`,
            pointerEvents: 'none',
          }}
        />
      ))}

      {/* Logo */}
      <Box
        sx={{
          animation: `${breathe} 3s ease-in-out infinite`,
          mb: 5,
        }}
      >
        <Logo style={{ width: 44, height: 44 }} />
      </Box>

      {/* Progress bar */}
      <Box sx={{ width: 200, mb: 3 }}>
        <LinearProgress
          variant="indeterminate"
          sx={{
            height: 2.5,
            borderRadius: 2,
            bgcolor: theme.progressTrack,
            '& .MuiLinearProgress-bar': {
              borderRadius: 2,
              bgcolor: theme.progressBar,
            },
          }}
        />
      </Box>

      {/* Status text */}
      <Typography
        sx={{
          fontSize: 13.5,
          fontWeight: 600,
          letterSpacing: '0.02em',
          background: theme.shimmer,
          backgroundSize: '200% auto',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          animation: `${shimmerText} 2.5s ease-in-out infinite`,
        }}
      >
        {t('loading_preparing_environment')}
      </Typography>
    </Box>
  );
};
